import { Link } from "wouter";
import { ArrowRight, Home, Search } from "lucide-react";
import { SeoHead } from "@/components/seo/seo-head";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { useLocale } from "@/lib/locale-context";

export default function NotFound() {
  const t = useT();
  const { localizePath } = useLocale();

  const popular: { href: string; label: string }[] = [
    { href: localizePath("/properties"), label: t("not_found.link_properties") },
    { href: localizePath("/r"), label: t("not_found.link_intelligence") },
    { href: localizePath("/market-benchmarks"), label: t("not_found.link_benchmarks") },
    { href: localizePath("/contact"), label: t("not_found.link_contact") },
  ];

  return (
    <>
      <SeoHead
        title={`${t("not_found.title")} — RiadIntel`}
        description={t("not_found.description")}
        noindex={true}
      />
      <section className="flex-1 flex items-center justify-center bg-background py-20 px-4">
        <div className="container max-w-3xl mx-auto text-center">
          <p
            className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-6"
            data-testid="not-found-eyebrow"
          >
            404 · {t("not_found.eyebrow")}
          </p>
          <h1
            className="text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-4"
            data-testid="not-found-title"
          >
            {t("not_found.title")}
          </h1>
          <p
            className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto mb-10"
            data-testid="not-found-description"
          >
            {t("not_found.description")}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
            <Button
              asChild
              className="rounded-none uppercase tracking-wider text-xs font-semibold h-11 px-6"
              data-testid="not-found-cta-home"
            >
              <Link href={localizePath("/")}>
                <Home className="w-4 h-4 mr-2" />
                {t("not_found.cta_home")}
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="rounded-none uppercase tracking-wider text-xs font-semibold h-11 px-6"
              data-testid="not-found-cta-properties"
            >
              <Link href={localizePath("/properties")}>
                <Search className="w-4 h-4 mr-2" />
                {t("not_found.cta_properties")}
              </Link>
            </Button>
          </div>

          <div className="border-t border-border pt-8">
            <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-4">
              {t("not_found.popular_title")}
            </h2>
            <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
              {popular.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="inline-flex items-center text-foreground hover:text-primary transition-colors"
                  >
                    {item.label}
                    <ArrowRight className="w-3 h-3 ml-1" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
